import React, { useState, useEffect } from 'react';
import MetricCard from './MetricCard';
import { supabase } from '../../../lib/supabase';
import { ELEVATED_CASE_PRIORITIES } from '../../../lib/casePriority';

const RoleBasedMetrics = ({ userRole, onMetricClick }) => {
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (userRole) fetchMetrics();
  }, [userRole]);

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      switch (userRole) {
        case 'admin':
          await fetchAdminMetrics();
          break;
        case 'staff':
          await fetchStaffMetrics();
          break;
        case 'dentist':
          await fetchDentistMetrics();
          break;
        case 'prosthetist':
          await fetchProsthetistMetrics();
          break;
        default:
          setMetrics([]);
      }
    } catch (err) {
      console.error('Error fetching metrics:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminMetrics = async () => {
    const [casesRes, urgentRes, usersRes, revenueRes] = await Promise.all([
      supabase.from('cases').select('id', { count: 'exact', head: true }),
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .in('priority', ELEVATED_CASE_PRIORITIES)
        .not('status', 'in', '("completed","cancelled")'),
      supabase.from('user_profiles').select('id', { count: 'exact', head: true }).eq('is_active', true),
      supabase
        .from('invoices')
        .select('total_amount')
        .eq('status', 'paid')
        .gte('issue_date', new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]),
    ]);

    const revenue = (revenueRes.data || []).reduce((sum, inv) => sum + (inv.total_amount || 0), 0);

    setMetrics([
      {
        title: 'Total des Cas',
        value: String(casesRes.count ?? 0),
        subtitle: 'Tous statuts confondus',
        icon: 'Briefcase',
        iconColor: 'var(--color-primary)',
        trend: 'up',
        trendValue: '',
        onClick: () => onMetricClick('/cases-list'),
      },
      {
        title: 'Conceptions Urgentes',
        value: String(urgentRes.count ?? 0),
        subtitle: 'Nécessitent une attention',
        icon: 'AlertCircle',
        iconColor: 'var(--color-error)',
        badge: urgentRes.count ? { type: 'urgent', count: urgentRes.count } : undefined,
        onClick: () => onMetricClick('/cases-list', { filter: 'urgent' }),
      },
      {
        title: 'Utilisateurs Actifs',
        value: String(usersRes.count ?? 0),
        subtitle: 'Comptes actifs',
        icon: 'Users',
        iconColor: 'var(--color-accent)',
        onClick: () => onMetricClick('/admin-users-management'),
      },
      {
        title: 'Revenus du Mois',
        value: new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(revenue),
        subtitle: 'Factures payées',
        icon: 'TrendingUp',
        iconColor: 'var(--color-success)',
        onClick: () => onMetricClick('/billing-overview'),
      },
    ]);
  };

  const fetchStaffMetrics = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;

    const [assignedRes, urgentRes, productionRes, messagesRes] = await Promise.all([
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .eq('assigned_staff_id', userId)
        .not('status', 'in', '("completed","cancelled")'),
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .eq('assigned_staff_id', userId)
        .in('priority', ELEVATED_CASE_PRIORITIES)
        .not('status', 'in', '("completed","cancelled")'),
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .eq('assigned_staff_id', userId)
        .eq('status', 'in_progress'),
      supabase
        .from('case_messages')
        .select('id', { count: 'exact', head: true })
        .eq('is_read', false)
        .eq('is_internal', false),
    ]);

    setMetrics([
      {
        title: 'Mes Cas Assignés',
        value: String(assignedRes.count ?? 0),
        subtitle: 'En cours de traitement',
        icon: 'Briefcase',
        iconColor: 'var(--color-primary)',
        onClick: () => onMetricClick('/cases-list', { filter: 'assigned' }),
      },
      {
        title: 'Conceptions Urgentes',
        value: String(urgentRes.count ?? 0),
        subtitle: 'Priorité haute',
        icon: 'AlertCircle',
        iconColor: 'var(--color-error)',
        badge: urgentRes.count ? { type: 'urgent', count: urgentRes.count } : undefined,
        onClick: () => onMetricClick('/cases-list', { filter: 'urgent' }),
      },
      {
        title: 'En Production',
        value: String(productionRes.count ?? 0),
        subtitle: 'Phase active',
        icon: 'Package',
        iconColor: 'var(--color-accent)',
        onClick: () => onMetricClick('/cases-list', { filter: 'production' }),
      },
      {
        title: 'Messages Non Lus',
        value: String(messagesRes.count ?? 0),
        subtitle: 'Nécessitent une réponse',
        icon: 'MessageSquare',
        iconColor: 'var(--color-warning)',
        onClick: () => onMetricClick('/cases-list', { filter: 'messages' }),
      },
    ]);
  };

  const fetchDentistMetrics = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;

    const [activeCasesRes, urgentRes, messagesRes] = await Promise.all([
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .eq('user_profile_id', userId)
        .not('status', 'in', '("completed","cancelled","draft")'),
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .eq('user_profile_id', userId)
        .in('priority', ELEVATED_CASE_PRIORITIES)
        .not('status', 'in', '("completed","cancelled")'),
      supabase
        .from('case_messages')
        .select('case_id, cases!inner(user_profile_id)', { count: 'exact', head: true })
        .eq('is_read', false)
        .eq('cases.user_profile_id', userId),
    ]);

    setMetrics([
      {
        title: 'Cas Actifs',
        value: String(activeCasesRes.count ?? 0),
        subtitle: 'En traitement',
        icon: 'Briefcase',
        iconColor: 'var(--color-accent)',
        onClick: () => onMetricClick('/cases-list', { filter: 'active' }),
      },
      {
        title: 'Conceptions Urgentes',
        value: String(urgentRes.count ?? 0),
        subtitle: 'Attention requise',
        icon: 'AlertCircle',
        iconColor: 'var(--color-error)',
        badge: urgentRes.count ? { type: 'urgent', count: urgentRes.count } : undefined,
        onClick: () => onMetricClick('/cases-list', { filter: 'urgent' }),
      },
      {
        title: 'Nouveaux Messages',
        value: String(messagesRes.count ?? 0),
        subtitle: 'Non lus',
        icon: 'MessageSquare',
        iconColor: 'var(--color-warning)',
        onClick: () => onMetricClick('/cases-list', { filter: 'messages' }),
      },
    ]);
  };

  const fetchProsthetistMetrics = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    const userId = user?.id;

    const [visibleRes, urgentRes, messagesRes, filesRes] = await Promise.all([
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .eq('prosthetist_id', userId),
      supabase
        .from('cases')
        .select('id', { count: 'exact', head: true })
        .eq('prosthetist_id', userId)
        .in('priority', ELEVATED_CASE_PRIORITIES)
        .not('status', 'in', '("completed","cancelled")'),
      supabase
        .from('case_messages')
        .select('id', { count: 'exact', head: true })
        .eq('is_read', false),
      supabase
        .from('case_documents')
        .select('id', { count: 'exact', head: true })
        .eq('is_active', true),
    ]);

    setMetrics([
      {
        title: 'Cas Visibles',
        value: String(visibleRes.count ?? 0),
        subtitle: 'Accès autorisé',
        icon: 'Eye',
        iconColor: 'var(--color-primary)',
        onClick: () => onMetricClick('/cases-list'),
      },
      {
        title: 'Conceptions Urgentes',
        value: String(urgentRes.count ?? 0),
        subtitle: 'Priorité haute',
        icon: 'AlertCircle',
        iconColor: 'var(--color-error)',
        badge: urgentRes.count ? { type: 'urgent', count: urgentRes.count } : undefined,
        onClick: () => onMetricClick('/cases-list', { filter: 'urgent' }),
      },
      {
        title: 'Messages',
        value: String(messagesRes.count ?? 0),
        subtitle: 'Conversations actives',
        icon: 'MessageSquare',
        iconColor: 'var(--color-accent)',
        onClick: () => onMetricClick('/cases-list', { filter: 'messages' }),
      },
      {
        title: 'Fichiers Partagés',
        value: String(filesRes.count ?? 0),
        subtitle: 'Documents disponibles',
        icon: 'FileText',
        iconColor: 'var(--color-success)',
        onClick: () => onMetricClick('/cases-list', { filter: 'files' }),
      },
    ]);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="bg-card border border-border rounded-lg p-6 animate-pulse">
            <div className="h-4 bg-muted rounded w-3/4 mb-4"></div>
            <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-muted rounded w-full"></div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metrics?.map((metric, index) => (
        <MetricCard key={index} {...metric} />
      ))}
    </div>
  );
};

export default RoleBasedMetrics;